import type { IntentChunkKind, WebMcpToolCandidate } from './types'

export interface SeedIntent {
  canonicalId: string
  kind: IntentChunkKind
  examples: string[]
  ontologyRefs?: string[]
  cogoJsonPaths?: string[]
  mcpCandidates?: WebMcpToolCandidate[]
}

export const SEED_INTENTS: SeedIntent[] = [
  {
    canonicalId: 'ui.target.selected.button',
    kind: 'target',
    examples: ['버튼', '선택한 버튼', '구매 버튼', 'button', 'cta', 'primary button'],
    ontologyRefs: ['cogo:Button', 'cogo:Control'],
    cogoJsonPaths: ['/blocks/*/components/*', '/manifest/pages/*'],
  },
  {
    canonicalId: 'style.change.color.primary',
    kind: 'style',
    examples: [
      '파란색으로 바꾸',
      '색상 변경',
      'primary color',
      'blue',
      '빨갛게',
      'background color',
    ],
    ontologyRefs: ['cogo:DesignToken', 'cogo:ColorToken'],
    cogoJsonPaths: ['/tokens/color', '/blocks/*/style', '/designSystem/tokens'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.applyAppPatch',
        score: 0.82,
        reason: 'style chunks mutate token or block style paths',
      },
    ],
  },
  {
    canonicalId: 'document.page.updateMeta',
    kind: 'operation',
    examples: [
      '페이지 제목 변경',
      '문서 페이지 제목 수정',
      'page title change',
      'update page details',
      'route 변경',
    ],
    ontologyRefs: ['cogo:Page', 'cogo:DocumentPage'],
    cogoJsonPaths: [
      '/manifest/pages/*/title',
      '/manifest/pages/*/description',
      '/manifest/pages/*/route',
    ],
    mcpCandidates: [
      {
        tool: 'cogo.studio.updatePageMeta',
        score: 0.84,
        reason: 'page/document detail chunks mutate page metadata through Studio MCP',
      },
    ],
  },
  {
    canonicalId: 'studio.publish.project',
    kind: 'operation',
    examples: ['배포해', '게시해', 'publish project', 'deploy', 'publish'],
    ontologyRefs: ['cogo:Publish', 'cogo:Deployment'],
    cogoJsonPaths: ['/metadata/publish', '/manifest'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.publishProject',
        score: 0.8,
        reason: 'publish chunks invoke the guarded Studio publish tool after confirmation',
      },
    ],
  },
  {
    canonicalId: 'figma.source.exportOriginal',
    kind: 'operation',
    examples: [
      '피그마 원본 내보내기',
      '가져온 fig 파일 다운로드',
      'export original figma source',
      'download source fig file',
    ],
    ontologyRefs: ['cogo:SourceDocument'],
    cogoJsonPaths: ['/sourceWorkspace/bundle/sourceArtifactRef'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.export_openpencil_source_file',
        score: 0.9,
        reason: 'source export returns the checksum-verified original import',
      },
    ],
  },
  {
    canonicalId: 'figma.source.editRevision',
    kind: 'operation',
    examples: [
      '피그마 소스 노드 편집',
      'figma source text node change',
      'source node 이름 변경',
      'edit retained fig source geometry',
    ],
    ontologyRefs: ['cogo:SourceDocument', 'cogo:SourceRevision'],
    cogoJsonPaths: ['/sourceWorkspace/bundle/revisionLineage'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.revise_openpencil_figma_source',
        score: 0.9,
        reason: 'bounded source edits export an immutable .fig revision and restart readback gates',
      },
    ],
  },
  {
    canonicalId: 'figma.source.applyMapping',
    kind: 'operation',
    examples: [
      '피그마 소스 매핑 승인',
      'figma source mapping',
      '화면 역할 매핑',
      'source screen role mapping',
    ],
    ontologyRefs: ['cogo:SourceDocument', 'cogo:SurfaceCandidate'],
    cogoJsonPaths: ['/sourceWorkspace/pageDecisions', '/sourceWorkspace/surfaceDecisions'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.apply_openpencil_suggested_mapping',
        score: 0.86,
        reason: 'source mapping chunks update the reversible OpenPencil source workspace',
      },
    ],
  },
  {
    canonicalId: 'figma.source.decideTokens',
    kind: 'operation',
    examples: [
      '피그마 소스 토큰 승인',
      'source token review',
      'design token candidates accept',
      '소스 토큰 결정',
    ],
    ontologyRefs: ['cogo:DesignToken'],
    cogoJsonPaths: ['/sourceWorkspace/tokenDecisions'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.set_all_openpencil_token_decisions',
        score: 0.84,
        reason: 'source token chunks update bounded token decisions',
      },
    ],
  },
  {
    canonicalId: 'figma.source.visualReview',
    kind: 'operation',
    examples: [
      '피그마 Gemma 시각 검토',
      'source preview visual review',
      '이미지 판정 실행',
      'run bounded vision judge',
    ],
    ontologyRefs: ['cogo:VisualEvidence', 'cogo:SurfaceCandidate'],
    cogoJsonPaths: ['/sourceWorkspace/visualJudgements'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.request_openpencil_visual_judgement',
        score: 0.88,
        reason: 'visual review chunks queue the bounded R2-backed Gemma judge',
      },
    ],
  },
  {
    canonicalId: 'figma.source.buildAssembly',
    kind: 'operation',
    examples: [
      '피그마 source assembly 생성',
      'build source manifest',
      '소스 어셈블리 구축',
      'create selected source assembly',
    ],
    ontologyRefs: ['cogo:ApplicationCompositionPlan'],
    cogoJsonPaths: ['/sourceWorkspace/assemblyManifest'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.build_openpencil_source_assembly',
        score: 0.86,
        reason: 'assembly chunks build the deterministic selected-source manifest',
      },
    ],
  },
  {
    canonicalId: 'actionflow.connect.checkout',
    kind: 'actionflow',
    examples: [
      '결제 플로우 연결',
      '구매 버튼 누르면 결제로 가게',
      'checkout flow',
      'connect payment flow',
      'payment action',
    ],
    ontologyRefs: ['cogo:ActionFlow', 'cogo:PaymentFlow'],
    cogoJsonPaths: ['/flows', '/blocks/*/events'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.applyAppPatch',
        score: 0.76,
        reason: 'ActionFlow binding requires validated app patch paths',
      },
      {
        tool: 'cogo.studio.addFlow',
        score: 0.72,
        reason: 'ActionFlow chunks may create or bind a flow',
      },
    ],
  },
  {
    canonicalId: 'copy.change.text',
    kind: 'copy',
    examples: ['문구 수정', '텍스트 바꿔', 'copy change', 'change label', '이름 변경'],
    ontologyRefs: ['cogo:Text', 'cogo:Label'],
    cogoJsonPaths: ['/blocks/*/components/*/props/text', '/blocks/*/copy'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.applyAppPatch',
        score: 0.78,
        reason: 'copy chunks mutate text-bearing component props',
      },
    ],
  },
  {
    canonicalId: 'data.bind.datasource',
    kind: 'data',
    examples: ['데이터 연결', 'datasource binding', 'API 붙여', 'supabase 연결', '목록 데이터'],
    ontologyRefs: ['cogo:Datasource', 'cogo:DataBinding'],
    cogoJsonPaths: ['/data', '/blocks/*/bindings', '/datasources'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.addDatasource',
        score: 0.74,
        reason: 'data chunks create or update block-scoped datasource declarations',
      },
    ],
  },
  {
    canonicalId: 'navigation.open.page',
    kind: 'navigation',
    examples: ['페이지 이동', 'navigate to page', 'route', '다음 페이지', 'open page'],
    ontologyRefs: ['cogo:Route', 'cogo:Page'],
    cogoJsonPaths: ['/manifest/pages', '/blocks/*/events'],
    mcpCandidates: [
      {
        tool: 'cogo.studio.navigate',
        score: 0.7,
        reason: 'navigation chunks open the requested Studio page/surface',
      },
    ],
  },
  {
    canonicalId: 'validation.run.proof',
    kind: 'validation',
    examples: ['검증해', '테스트 돌려', 'proof', 'readback', 'validation check'],
    ontologyRefs: ['cogo:Proof', 'cogo:Validation'],
    cogoJsonPaths: ['/metadata/proof', '/metadata/validation'],
  },
  {
    canonicalId: 'locale.translate.app',
    kind: 'locale',
    examples: ['다국어', '한국어 지원', '영어로 번역', 'translate', 'localize', 'i18n'],
    ontologyRefs: ['cogo:Locale', 'cogo:I18n'],
    cogoJsonPaths: ['/metadata/i18n', '/manifest/locales'],
  },
]
